// src/pages/DocumentManagementSystem/utils/dmsRequiredFields.js
//
// The mandatory fields of a DMS document, in ONE place.
//
// Owner (2026-10-02): "in DMS we have to do mandatory fields ... you decide."
// The rules are the legacy program's submit check (DMSNew.vb CheckValidity,
// lines 1303-1452), which the API already enforces one message at a time
// (DmsService.ValidateForSubmitAsync). The form now shows them up front: a red
// asterisk on every required field, and before sending it lists EVERYTHING that
// is missing at once, like the old program's message box did.
//
//   Send for approval (every type): title, author, manager, at least one
//     approver (not the author).
//   ECN:  Description of Change, Reasons for Change.
//   SOP / WI / FCD: the Procedure / Work Instruction / Form text.
//   LIT:  a literature type, and Notes or at least one attachment.
//   Revision Log (SOP / WI / FCD / LIT, when the document already has a revision
//     log — the API's revLogRequired): Section / Paragraph Changed, Change Made
//     (10 characters or more) and Training Required.
//   Save Draft / Save & Close: the title (and the literature type for LIT), as in
//     legacy ("Please enter title before saving.").

/** Visible text of an editor value: tags, &nbsp; and blanks removed. */
export function plainText(html) {
    return String(html ?? "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim();
}

/** The body tab key per document type (null for ECN). */
export function bodyTabKey(docType) {
    switch ((docType || "").toLowerCase()) {
        case "sop": return "procedure";
        case "wi": return "workInstruction";
        case "fcd": return "form";
        case "lit": return "notes";
        default: return null;
    }
}

const BODY_LABEL = { procedure: "Procedure", workInstruction: "Work Instruction", form: "Form", notes: "Notes" };

/** Tabs that hold at least one required field (they get the red asterisk). */
export function requiredTabKeys(docType, revLogRequired) {
    const dt = (docType || "").toLowerCase();
    const keys = new Set(["properties"]);
    if (dt === "ecn") { keys.add("header"); keys.add("details"); return keys; }
    const body = bodyTabKey(dt);
    if (body) keys.add(body);
    if (revLogRequired) keys.add("revlog");
    return keys;
}

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Everything missing before a document can be sent for approval.
 * @returns {{ field: string, tab: string|null, tabLabel: string|null, message: string }[]}
 */
export function collectSubmitProblems({ docType, form, revLogRequired, tabs, logoType }) {
    const dt = (docType || "").toLowerCase();
    const f = form || {};
    const props = f.properties || {};
    const labelOf = (key) => (tabs || []).find((t) => t.key === key)?.label || null;
    const out = [];
    const add = (field, tab, message) => out.push({ field, tab, tabLabel: tab ? labelOf(tab) : null, message });

    // Owner (2026-10-11): the logo (Document Branding) is mandatory - the PDF carries it.
    if (!String(logoType || "").trim()) add("logoType", null, "Document Branding (choose a logo)");

    // Title: the ECN Title for an ECN (it is the document title), else Document Title.
    if (dt === "ecn") {
        if (!String(f.header?.title || "").trim()) add("header.title", "header", "ECN Title");
    } else if (!String(f.docTitle || "").trim()) {
        add("docTitle", null, "Document Title");
    }

    if (dt === "lit" && !String(f.literatureType || "").trim()) add("literatureType", null, "Literature Type");

    // People (Properties tab)
    const authorId = num(props.authorId);
    if (!authorId) add("properties.authorId", "properties", "Document Author");
    if (!num(props.managerId)) add("properties.managerId", "properties", "Document Manager");
    const approvers = (props.approvers || []).map(Number).filter((n) => n > 0);
    if (approvers.length === 0) add("properties.approvers", "properties", "At least one approver");
    else if (authorId && approvers.includes(authorId))
        add("properties.approvers", "properties", "An approver other than the author (the author cannot approve their own document)");

    if (dt === "ecn") {
        const d = f.details || {};
        const hasContent = (html) => plainText(html).length > 0 || /<img\b/i.test(String(html || ""));
        if (!hasContent(d.descriptionOfChange)) add("details.descriptionOfChange", "details", "Description of Change");
        if (!hasContent(d.reasonsForChange)) add("details.reasonsForChange", "details", "Reasons for Change");
    } else {
        const body = bodyTabKey(dt);
        if (dt === "lit") {
            const hasNotes = plainText(f.notes).length > 0;
            const hasFiles = Array.isArray(f.attachments) && f.attachments.length > 0;
            if (!hasNotes && !hasFiles) add("notes", "notes", "Notes, or at least one attachment");
        } else if (body && plainText(f[body]).length === 0) {
            add(body, body, `${BODY_LABEL[body]} text`);
        }

        if (revLogRequired) {
            if (!String(f.sectionChanged || "").trim()) add("sectionChanged", "revlog", "Section / Paragraph Changed");
            const made = String(f.changeMade || "").trim();
            if (made.length < 10) add("changeMade", "revlog", made.length === 0 ? "Description of Change Made" : "Description of Change Made (at least 10 characters)");
            if (!String(f.trainingRequired || "").trim()) add("trainingRequired", "revlog", "Training Required (Yes / No / Optional / N/A)");
        }
    }
    return out;
}

/** What a draft needs before it can be saved (legacy: title, and the type for LIT). */
export function collectDraftProblems({ docType, form, tabs, logoType }) {
    const dt = (docType || "").toLowerCase();
    const f = form || {};
    const labelOf = (key) => (tabs || []).find((t) => t.key === key)?.label || null;
    const out = [];
    // Owner (2026-10-11): a draft needs its logo too.
    if (!String(logoType || "").trim()) out.push({ field: "logoType", tab: null, tabLabel: null, message: "Document Branding (choose a logo)" });
    if (dt === "ecn") {
        if (!String(f.header?.title || "").trim()) out.push({ field: "header.title", tab: "header", tabLabel: labelOf("header"), message: "ECN Title" });
    } else if (!String(f.docTitle || "").trim()) {
        out.push({ field: "docTitle", tab: null, tabLabel: null, message: "Document Title" });
    }
    if (dt === "lit" && !String(f.literatureType || "").trim())
        out.push({ field: "literatureType", tab: null, tabLabel: null, message: "Literature Type" });
    return out;
}

/** { field: message } for the form controls. */
export function problemMap(problems) {
    const m = {};
    for (const p of problems || []) if (!m[p.field]) m[p.field] = p.message;
    return m;
}
